<template>
  <div class="space-y-2" data-test="phone-book-picker">
    <div class="flex items-center gap-2">
      <input
        v-model="search"
        type="search"
        placeholder="Search phone book"
        aria-label="Search phone book"
        data-demo-search
        data-spoof-kind="person"
        data-test="phone-book-search"
        class="sensitive flex-1 min-w-0 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2"
        :class="theme.input"
      />
      <div class="flex rounded-lg p-0.5 shrink-0" :class="theme.tabs" role="tablist" aria-label="Phone book type">
        <button
          v-for="option in typeOptions"
          :key="option.value"
          type="button"
          role="tab"
          :aria-selected="typeFilter === option.value"
          :data-test="`phone-book-type-${option.value}`"
          class="px-2 py-1 text-xs font-medium rounded-md transition-colors"
          :class="typeFilter === option.value ? theme.tabActive : theme.tab"
          @click="setType(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <p v-if="error" class="text-xs" :class="theme.error" data-test="phone-book-error">{{ error }}</p>
    <div v-else-if="loading && !entries.length" class="border rounded-lg overflow-hidden" :class="theme.list" aria-busy="true" aria-label="Loading phone book" data-test="phone-book-skeleton">
      <div v-for="n in 4" :key="n" class="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 animate-pulse" :class="theme.row">
        <span class="flex-1 space-y-1.5">
          <span class="block h-3 rounded" :class="[theme.skeleton, n % 2 ? 'w-2/5' : 'w-1/2']" />
          <span class="block h-2.5 w-1/5 rounded" :class="theme.skeleton" />
        </span>
        <span class="h-2.5 w-20 rounded" :class="theme.skeleton" />
      </div>
    </div>
    <p v-else-if="!groups.length && isFiltered" class="text-xs text-gray-400" data-test="phone-book-no-matches">No matches.</p>
    <p v-else-if="!groups.length" class="text-xs text-gray-400">{{ emptyText }}</p>
    <div v-else :aria-busy="loading" class="max-h-72 overflow-y-auto border rounded-lg" :class="theme.list" role="listbox" aria-label="Phone book">
      <div v-for="group in groups" :key="group.type">
        <p class="sticky top-0 px-3 py-1 text-[11px] font-semibold uppercase border-b" :class="theme.groupHeader">
          {{ group.label }} <span class="font-normal">({{ group.entries.length }})</span>
        </p>
        <button
          v-for="entry in group.entries"
          :key="entry.key"
          type="button"
          role="option"
          :aria-selected="modelValue === entry.key"
          :data-test="`phone-book-entry-${entry.key}`"
          class="w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm border-b last:border-b-0 transition-colors focus:outline-none"
          :class="[theme.row, modelValue === entry.key ? theme.rowActive : theme.rowIdle]"
          @click="pick(entry)"
        >
          <span class="min-w-0 flex-1">
            <span class="sensitive block truncate font-medium" :class="theme.name" :data-spoof-kind="entry.type === 'internal' ? 'person' : 'practice'">{{ entry.name }}</span>
            <span class="block truncate text-xs" :class="theme.muted">{{ entry.type === 'internal' ? 'Internal' : entry.role?.display_name }}</span>
            <DemoText v-if="entry.notes" tag="span" class="block text-xs whitespace-pre-line" :class="theme.muted" :text="entry.notes" />
          </span>
          <span class="sensitive shrink-0 text-xs" :class="theme.muted" data-spoof-kind="phone">{{ formatPhone(entry.phone_number) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DemoText from '@/components/DemoMode/DemoText.vue'
import demoProtectedFields from '@/mixins/demoProtectedFields'
import { searchPhoneBook } from '@/services/api/telehealthPhoneBook'
import { parseTelehealthError } from '@/services/api/telehealthErrors'
import { formatPhone } from '@/utils/phoneBookDirectory'

/** @typedef {import('@/services/api/schema').components['schemas']['PhoneBookDirectoryEntry']} PhoneBookDirectoryEntry */

export const SEARCH_DEBOUNCE_MS = 250

const THEMES = {
  light: {
    input: 'border-gray-300 bg-white text-gray-900 focus:ring-blue-400',
    tabs: 'bg-gray-100',
    tab: 'text-gray-600 hover:text-gray-900',
    tabActive: 'bg-white shadow-sm text-gray-900',
    error: 'text-red-600',
    list: 'border-gray-200 bg-white',
    groupHeader: 'bg-gray-50 text-gray-500 border-gray-200',
    row: 'border-gray-100 focus:bg-blue-50',
    rowIdle: 'hover:bg-gray-50',
    rowActive: 'bg-blue-50',
    name: 'text-neutral-800',
    muted: 'text-gray-500',
    skeleton: 'bg-gray-200',
  },
  dark: {
    input: 'border-gray-600 bg-gray-800 text-white placeholder-gray-500 focus:ring-blue-500',
    tabs: 'bg-gray-800',
    tab: 'text-gray-400 hover:text-gray-200',
    tabActive: 'bg-gray-600 shadow-sm text-white',
    error: 'text-red-400',
    list: 'border-gray-600 bg-gray-900',
    groupHeader: 'bg-gray-800 text-gray-400 border-gray-700',
    row: 'border-gray-800 focus:bg-blue-600/20',
    rowIdle: 'hover:bg-gray-800',
    rowActive: 'bg-blue-600/20',
    name: 'text-white',
    muted: 'text-gray-400',
    skeleton: 'bg-gray-700',
  },
}

const GROUPS = [
  { type: 'internal', label: 'Internal' },
  { type: 'contact', label: 'Contacts' },
]

export default {
  name: 'PhoneBookPicker',
  components: { DemoText },
  mixins: [demoProtectedFields],
  props: {
    modelValue: { type: String, default: null },
    emptyText: { type: String, default: 'The phone book is empty.' },
    /** Dark colours for dark panels (the clinical engagement Call tab). */
    dark: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'select'],
  demoProtectedFields: ['search'],
  data() {
    return {
      search: '',
      typeFilter: 'all',
      /** @type {PhoneBookDirectoryEntry[]} */
      entries: [],
      loading: true,
      error: '',
    }
  },
  computed: {
    theme() {
      return THEMES[this.dark ? 'dark' : 'light']
    },
    typeOptions() {
      return [{ value: 'all', label: 'All' }, { value: 'internal', label: 'Internal' }, { value: 'contact', label: 'Contacts' }]
    },
    isFiltered() {
      return !!this.search.trim() || this.typeFilter !== 'all'
    },
    groups() {
      return GROUPS.map((group) => ({ ...group, entries: this.entries.filter((entry) => entry.type === group.type) })).filter(
        (group) => group.entries.length,
      )
    },
  },
  watch: {
    search() {
      this.scheduleLoad()
    },
  },
  created() {
    this.requestSeq = 0
    this.searchTimer = null
  },
  mounted() {
    this.load()
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer)
    this.requestSeq += 1
  },
  methods: {
    formatPhone,
    setType(type) {
      if (type === this.typeFilter) return
      this.typeFilter = type
      this.load()
    },
    scheduleLoad() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.load(), SEARCH_DEBOUNCE_MS)
    },
    /** Refetch now with the current search and type (e.g. after a contact was added). */
    reload() {
      return this.load()
    },
    async load() {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
      const requestId = ++this.requestSeq
      const body = {
        query: this.search.trim(),
        types: this.typeFilter === 'all' ? ['internal', 'contact'] : [this.typeFilter],
      }
      this.loading = true
      try {
        const entries = await searchPhoneBook(body)
        if (requestId !== this.requestSeq) return
        this.entries = entries
        this.error = ''
        const selected = this.modelValue && entries.find((entry) => entry.key === this.modelValue)
        if (selected) this.$emit('select', selected)
      } catch (err) {
        if (requestId !== this.requestSeq) return
        this.entries = []
        this.error = parseTelehealthError(err, 'Failed to load phone book').summary
      } finally {
        if (requestId === this.requestSeq) this.loading = false
      }
    },
    /** @param {PhoneBookDirectoryEntry} entry */
    pick(entry) {
      this.$emit('update:modelValue', entry.key)
      this.$emit('select', entry)
    },
  },
}
</script>
